<template>
  <div>
    <vx-card class="mb-base" :title="t('nav_account')">
      <div class="cp-facts">
        <div>
          <div class="cp-fact-label">{{ t('email') }}</div>
          <div class="cp-fact-value">{{ portal.me && portal.me.email }}</div>
        </div>
        <div>
          <div class="cp-fact-label">{{ t('companies') }}</div>
          <div v-for="c in (portal.me ? portal.me.companies : [])" :key="c.id" class="cp-fact-value">{{ c.name }}</div>
        </div>
      </div>
    </vx-card>

    <!-- Pas de mot de passe ni de 2FA en aperçu admin : ce serait le compte de l'admin. -->
    <template v-if="!(portal.me && portal.me.preview)">
      <div class="vx-row">
        <!-- Mot de passe -->
        <div class="vx-col w-full lg:w-1/2 mb-base">
          <vx-card :title="t('change_password')">
            <form class="cp-form" @submit.prevent="savePassword">
              <vs-input type="password" autocomplete="current-password" :label="t('current_password')" v-model="pwd.current" class="w-full" />
              <vs-input type="password" autocomplete="new-password" :label="t('new_password')" v-model="pwd.next" class="w-full" />
              <vs-input type="password" autocomplete="new-password" :label="t('confirm_password')" v-model="pwd.confirm" class="w-full" />
              <p class="cp-muted text-sm">{{ t('password_rules') }}</p>
              <vs-alert v-if="pwd.error" color="danger" active="true">{{ pwd.error }}</vs-alert>
              <vs-alert v-if="pwd.done" color="success" active="true">{{ t('password_changed') }}</vs-alert>
              <div>
                <vs-button button="submit" :disabled="pwd.busy || !pwd.current || !pwd.next || !pwd.confirm">{{ t('save') }}</vs-button>
              </div>
            </form>
          </vx-card>
        </div>

        <!-- Double authentification : facultative pour un client (Chady, 2026-11-03). -->
        <div class="vx-col w-full lg:w-1/2 mb-base">
          <vx-card :title="t('twofa_title')">
            <div v-if="twofa.status === null" class="cp-muted">{{ t('loading') }}</div>
            <template v-else>
              <div class="flex flex-wrap items-center mb-3">
                <vs-chip v-if="twofa.status.twofa_enabled" color="success">
                  {{ t('twofa_on') }} · {{ twofa.status.twofa_method === 'totp' ? t('twofa_method_totp') : t('twofa_method_email') }}
                </vs-chip>
                <vs-chip v-else color="dark">{{ t('twofa_off') }}</vs-chip>
              </div>
              <p class="cp-muted text-sm mb-4">{{ t('twofa_optional') }}</p>

              <!-- Activation : assistant partagé de l'ERP (méthode → code → codes de secours). -->
              <template v-if="!twofa.status.twofa_enabled">
                <div v-if="twofa.setup">
                  <TwoFASetupSteps @done="onActivated" @session-expired="onSessionExpired" />
                  <vs-button type="flat" class="mt-3" @click="twofa.setup = false">{{ t('cancel') }}</vs-button>
                </div>
                <vs-button v-else color="success" icon-pack="feather" icon="icon-shield" @click="twofa.setup = true">{{ t('twofa_enable') }}</vs-button>
              </template>

              <!-- Désactivation : mot de passe + un code (règle du serveur). -->
              <template v-else>
                <form v-if="twofa.disabling" class="cp-form" @submit.prevent="disableTwofa">
                  <vs-input type="password" autocomplete="current-password" :label="t('current_password')" v-model="twofa.password" class="w-full" />
                  <div class="flex flex-wrap items-end">
                    <vs-input :label="t('twofa_code')" v-model="twofa.code" class="mr-3" autocomplete="one-time-code" />
                    <vs-button v-if="twofa.status.twofa_method === 'email'" type="border" size="small" :disabled="twofa.busy" @click="sendCode">{{ t('twofa_send_code') }}</vs-button>
                  </div>
                  <p v-if="twofa.codeSent" class="cp-muted text-sm">{{ t('twofa_code_sent') }}</p>
                  <vs-alert v-if="twofa.error" color="danger" active="true">{{ twofa.error }}</vs-alert>
                  <div>
                    <vs-button button="submit" color="danger" :disabled="twofa.busy || !twofa.password || !twofa.code">{{ t('twofa_disable') }}</vs-button>
                    <vs-button type="flat" class="ml-2" @click="twofa.disabling = false">{{ t('cancel') }}</vs-button>
                  </div>
                </form>
                <vs-button v-else type="border" color="danger" icon-pack="feather" icon="icon-shield-off" @click="startDisable">{{ t('twofa_disable') }}</vs-button>
              </template>
            </template>
          </vx-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
// Portail client → mon compte : e-mail, sociétés visibles, mot de passe
// (/api/profile/password, règles NIST côté serveur) et double authentification
// FACULTATIVE : activation par l'assistant partagé de l'ERP (TwoFASetupSteps),
// désactivation avec mot de passe + code. roleGate ouvre ces routes au client.
import portalPage from '@/views/client/portalPage.js'
import TwoFASetupSteps from '@/components/twofa/TwoFASetupSteps.vue'
import twofaApi from '@/http/requests/auth/twofa.js'
import { errorMessage } from '@/views/client/portalApi.js'

export default {
  name: 'ClientAccount',
  components: { TwoFASetupSteps },
  mixins: [portalPage],
  data () {
    return {
      pwd: { current: '', next: '', confirm: '', busy: false, error: null, done: false },
      twofa: { status: null, setup: false, disabling: false, password: '', code: '', codeSent: false, busy: false, error: null }
    }
  },
  methods: {
    async savePassword () {
      this.pwd.error = null
      this.pwd.done = false
      if (this.pwd.next !== this.pwd.confirm) {
        this.pwd.error = this.t('password_mismatch')
        return
      }
      this.pwd.busy = true
      try {
        await twofaApi.changePassword({ currentPassword: this.pwd.current, newPassword: this.pwd.next })
        this.pwd.current = this.pwd.next = this.pwd.confirm = ''
        this.pwd.done = true
      } catch (err) {
        // Règles de mot de passe : message du serveur (anglais), seul à connaître la raison.
        this.pwd.error = await errorMessage(err, this.t('load_error'))
      } finally {
        this.pwd.busy = false
      }
    },
    async loadTwofa () {
      try {
        this.twofa.status = (await twofaApi.status()).data
      } catch (e) {
        this.twofa.status = { twofa_enabled: false, twofa_method: null }
      }
    },
    onActivated () {
      this.twofa.setup = false
      this.notify('success', this.t('twofa_enabled_ok'))
      this.loadTwofa()
    },
    onSessionExpired () {
      this.$router.push('/pages/login').catch(() => {})
    },
    startDisable () {
      Object.assign(this.twofa, { disabling: true, password: '', code: '', codeSent: false, error: null })
    },
    async sendCode () {
      this.twofa.busy = true
      this.twofa.error = null
      try {
        await twofaApi.sendEmailChallenge()
        this.twofa.codeSent = true
      } catch (err) {
        this.twofa.error = await errorMessage(err, this.t('load_error'))
      } finally {
        this.twofa.busy = false
      }
    },
    async disableTwofa () {
      this.twofa.busy = true
      this.twofa.error = null
      try {
        await twofaApi.disable({ currentPassword: this.twofa.password, code: this.twofa.code })
        this.twofa.disabling = false
        this.notify('success', this.t('twofa_disabled_ok'))
        await this.loadTwofa()
      } catch (err) {
        this.twofa.error = await errorMessage(err, this.t('load_error'))
      } finally {
        this.twofa.busy = false
      }
    },
    notify (color, text) {
      this.$vs.notify({ title: this.t('twofa_title'), text, color, iconPack: 'feather', icon: 'icon-shield', position: 'bottom-right', time: 6000 })
    }
  },
  created () {
    this.loading = false
    if (!(this.portal.me && this.portal.me.preview)) this.loadTwofa()
  }
}
</script>

<style scoped>
.cp-form {
  display: grid;
  gap: 14px;
}
</style>
